import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { FaProjectDiagram } from '@react-icons/all-files/fa/FaProjectDiagram';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import ProfaseModal from '../../commons/ProfaseModal';
import { profaseDataGridSx } from '../../commons/profaseDataGridTheme';
import { formatWorkflowPeriodicidadLabel } from '../../shared/workflow-periodicidad';
import { PROCESOS_EMPRESA_PAGE_SIZE } from './procesos-empresa-routes';

export interface ProcesosEmpresaWorkflowRow {
  id: number;
  workflow?: {
    id?: number;
    nombre?: string;
    periodicidad?: string | null;
    workflowClase?: { nombre?: string } | null;
  } | null;
}

export interface ProcesosEmpresaWorkflowsViewProps {
  empresaId: number;
  empresaLabel?: string;
  active?: boolean;
  onSelectWorkflow: (workflow: { id: number; nombre?: string }) => void;
}

export function ProcesosEmpresaWorkflowsView({
  empresaId,
  empresaLabel,
  active = true,
  onSelectWorkflow,
}: ProcesosEmpresaWorkflowsViewProps) {
  const [rows, setRows] = useState<ProcesosEmpresaWorkflowRow[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: PROCESOS_EMPRESA_PAGE_SIZE,
  });

  const fetchWorkflows = useCallback(async () => {
    if (!active || !empresaId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('empresaId', String(empresaId));
      params.set('page', String(paginationModel.page));
      params.set('size', String(PROCESOS_EMPRESA_PAGE_SIZE));
      params.set('sort', 'id,desc');
      params.set('cacheBuster', String(Date.now()));
      const res = await axios.get<ProcesosEmpresaWorkflowRow[]>(
        `api/profase-workflow-empresas?${params.toString()}`,
      );
      setRows(Array.isArray(res.data) ? res.data : []);
      setTotalItems(parseInt(String(res.headers['x-total-count'] ?? '0'), 10) || 0);
    } catch {
      setRows([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, [active, empresaId, paginationModel.page]);

  useEffect(() => {
    void fetchWorkflows();
  }, [fetchWorkflows]);

  const selectWorkflow = useCallback((row: ProcesosEmpresaWorkflowRow) => {
    const workflowId = row.workflow?.id;
    if (workflowId == null) return;
    onSelectWorkflow({ id: workflowId, nombre: row.workflow?.nombre });
  }, [onSelectWorkflow]);

  const cols: GridColDef[] = useMemo(
    () => [
      { field: 'id', headerName: 'ID', flex: 0.35, minWidth: 56, sortable: false },
      {
        field: 'workflow.nombre',
        headerName: 'Workflow',
        flex: 1.4,
        minWidth: 160,
        sortable: false,
        renderCell: params => {
          const row = params.row as ProcesosEmpresaWorkflowRow;
          const label = row.workflow?.nombre?.trim() || (row.workflow?.id != null ? `Workflow #${row.workflow.id}` : '—');
          return (
            <button
              type="button"
              className="btn btn-link p-0 text-start"
              style={{ fontSize: 13, textDecoration: 'none' }}
              onClick={() => selectWorkflow(row)}
            >
              {label}
            </button>
          );
        },
      },
      {
        field: 'workflow.periodicidad',
        headerName: 'Periodicidad',
        flex: 0.7,
        minWidth: 110,
        sortable: false,
        renderCell: params => formatWorkflowPeriodicidadLabel(
          (params.row as ProcesosEmpresaWorkflowRow).workflow?.periodicidad,
        ),
      },
      {
        field: 'workflow.workflowClase',
        headerName: 'Clase',
        flex: 0.8,
        minWidth: 110,
        sortable: false,
        renderCell: params => {
          const nombre = (params.row as ProcesosEmpresaWorkflowRow).workflow?.workflowClase?.nombre?.trim();
          return nombre || <span style={{ color: 'var(--crm-text-muted)' }}>—</span>;
        },
      },
    ],
    [selectWorkflow],
  );

  return (
    <>
      <div style={s.info}>
        <div style={s.infoItem}>
          <span style={s.infoLabel}>Empresa</span>
          <span style={s.infoValue}>{empresaLabel?.trim() || `Empresa #${empresaId}`}</span>
        </div>
      </div>
      <p style={s.hint}>Seleccione un workflow para ver los periodos generados de esta empresa.</p>
      <DataGrid
        rows={rows}
        columns={cols}
        getRowId={row => row.id}
        autoHeight
        pagination
        paginationMode="server"
        disableRowSelectionOnClick
        disableColumnMenu
        paginationModel={paginationModel}
        onPaginationModelChange={model => {
          setPaginationModel({ ...model, pageSize: PROCESOS_EMPRESA_PAGE_SIZE });
        }}
        onRowClick={params => selectWorkflow(params.row as ProcesosEmpresaWorkflowRow)}
        rowCount={totalItems}
        loading={loading}
        pageSizeOptions={[PROCESOS_EMPRESA_PAGE_SIZE]}
        localeText={{ noRowsLabel: 'Esta empresa no tiene workflows asociados' }}
        sx={{
          ...profaseDataGridSx,
          '& .MuiDataGrid-row': { cursor: 'pointer' },
        }}
      />
    </>
  );
}

interface ProcesosEmpresaWorkflowsModalProps {
  open: boolean;
  empresaId: number;
  empresaLabel?: string;
  onClose: () => void;
  onBack: () => void;
  onSelectWorkflow: (workflow: { id: number; nombre?: string }) => void;
}

export default function ProcesosEmpresaWorkflowsModal({
  open,
  empresaId,
  empresaLabel,
  onClose,
  onBack,
  onSelectWorkflow,
}: ProcesosEmpresaWorkflowsModalProps) {
  return (
    <ProfaseModal
      open={open}
      titleId="procesos-empresa-workflows-modal-title"
      size="wide"
      scroll
    >
      <div style={s.header}>
        <h2 id="procesos-empresa-workflows-modal-title" className="profase-modal-title m-0">
          <FaProjectDiagram style={{ marginRight: 8, verticalAlign: '-2px' }} />
          Workflows
        </h2>
        <div style={s.headerActions}>
          <button type="button" className="profase-form-btn-secondary btn-sm" onClick={onBack}>
            Empresas
          </button>
          <button type="button" className="profase-form-btn-secondary btn-sm" onClick={onClose}>
            Cerrar
          </button>
        </div>
      </div>
      <ProcesosEmpresaWorkflowsView
        empresaId={empresaId}
        empresaLabel={empresaLabel}
        active={open}
        onSelectWorkflow={onSelectWorkflow}
      />
    </ProfaseModal>
  );
}

const s: Record<string, React.CSSProperties> = {
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 8,
    flexWrap: 'wrap',
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  info: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 16,
    margin: '8px 0 16px',
    padding: '12px 14px',
    borderRadius: 10,
    background: 'var(--crm-surface-muted, #f8fafc)',
  },
  infoItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    minWidth: 140,
  },
  infoLabel: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: 'var(--crm-text-secondary)',
  },
  infoValue: {
    fontSize: 14,
    fontWeight: 600,
    color: 'var(--crm-body-text, #0f172a)',
  },
  hint: {
    margin: '0 0 12px',
    fontSize: 13,
    color: 'var(--crm-text-secondary)',
  },
};
